import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Component_GenericRenderer, EditableDBItemV3 } from '@nu-art/thunderstorm-frontend/index';
import { TS_HorizontalWorkspace } from '@nu-art/ts-workspace-frontend/index';
import { workspaceKey_ManagerPathway } from './workspace-config.js';
import './Page_PathwayEditor.scss';
import { PathwaySelector_Search } from './selectors/PathwaySelector_Search.js';
import { ImplementationMissingException, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { RoutePermissions } from '@nu-art/permissions-frontend/index';
import { Renderer_PathwayEditor } from './selectors/renderers/renderers-pathway/Renderer_PathwayEditor.js';
import { Renderer_PathwayStateDetails } from './selectors/renderers/renderers-pathway-state/Renderer_PathwayStateDetails/Renderer_PathwayStateDetails.js';
import { Renderer_PathwayStateEditor } from './selectors/renderers/renderers-pathway-state/Renderer_PathwayStateEditor.js';
import { Renderer_PathwayDetails } from './selectors/renderers/renderers-pathway/Renderer_PathwayDetails/Renderer_PathwayDetails.js';
import { Renderer_PathwayTransitionEditor } from './selectors/renderers/renderers-pathway-transition/Renderer_PathwayTransitionEditor.js';
import { Renderer_PathwayTransitionDetails } from './selectors/renderers/renderers-pathway-transition/Renderer_PathwayTransitionDetails.js';
import { Empty_PathwayState } from './consts.js';
import { Page_BaseManagerUI } from '@app/styles/frontend/ui/ui-manager/Page/Page_BaseManagerUI';
import { Component_SwitchView } from '@nu-art/permissions-frontend/ui/Component_SwitchView';
import { Presenter_GraphViewWrapper } from './presenters/Presenter_GraphViewWrapper.js';
import { dispatch_showResolutionFunctionEditorPanel } from '../Component_ResolutionFunctionEditor/dispatchers.js';
import { ModuleFE_Action, ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition, } from '../../_entity.js';
import { ModulePackFE_Pathway } from '../../module-pack.js';
import { Renderer_SearchResult, SearchItemV3_With_ID } from '@app/styles';
import { Component_ResolutionFunctionEditor } from '../Component_ResolutionFunctionEditor/Component_ResolutionFunctionEditor.js';
import { Pathway_GraphView } from '../Pathway_GraphView/Pathway_GraphView.js';
import { basicSyncEventRefresh } from './utils.js';
import { ModuleFE_PathwayEditor } from '../../modules/index.js';
import { ModuleFE_Expression, ModuleFE_Value, ModuleFE_Variable } from '@app/hcs-frontend';
import { ModuleFE_DiseaseProfile } from '@app/dp-frontend';
import { Panel_ExpressionSidePanel, UtilsTab_PrettyPrintV2 } from '@app/hcs-frontend/expression';
import { Renderer_PathwaySearchItem } from './Renderer_PathwaySearchItem.js';
const viewKey = 'view';
const editKey = 'edit';
const EditorKey_Pathway = 'pathway-editor';
export class Page_PathwayEditor extends Page_BaseManagerUI {
    // ######################## Static ########################
    static Route = {
        key: EditorKey_Pathway,
        path: 'pathway-v2',
        Component: this,
        enabled: RoutePermissions(ModuleFE_Pathway.permissions.ui.Release),
        modulesToAwait: [...ModulePackFE_Pathway,
            ModuleFE_Action,
            ModuleFE_Value,
            ModuleFE_Variable,
            ModuleFE_Expression,
            ModuleFE_DiseaseProfile],
    };
    static searchItemKey_Pathway = ModuleFE_Pathway.dbDef.dbKey;
    static SearchableItem_Pathway = SearchItemV3_With_ID({
        module: ModuleFE_Pathway,
        type: Page_PathwayEditor.searchItemKey_Pathway,
        route: Page_PathwayEditor.Route,
        entityName: ModuleFE_Pathway.dbDef.entityName,
        entityLabel: 'Pathway',
        groupId: ['Advisor'],
        mapper: (item) => [item.label],
        tags: [],
        resultRenderer: (item) => {
            return _jsx(Pathway_GraphView, { pathwayId: item });
        },
        renderer: (item, selected, onClickCallback) => {
            return _jsx(Renderer_SearchResult, { itemId: item._id, itemKey: Page_PathwayEditor.searchItemKey_Pathway, itemLabel: item.label, entityLabel: 'Pathway', hierarchy: 'Pathways', selected: selected, onClickCallback: onClickCallback, orgRenderer: _jsx(Component_GenericRenderer, { rendererKey: 'organization-permission-editor', props: {
                        item: item,
                        indicatorMode: true,
                        orgNameClassName: 'search-result__hierarchy'
                    } }) });
        }
    });
    static SearchableItem_PathwayV2 = SearchItemV3_With_ID({
        ...Page_PathwayEditor.SearchableItem_Pathway,
        renderer: (item, selected, onClickCallback) => _jsx(Renderer_PathwaySearchItem, { item: item, selected: selected, onClickCallback: onClickCallback })
    });
    static defaultProps = {
        searchItems: [Page_PathwayEditor.SearchableItem_PathwayV2]
    };
    // ######################## Props ########################
    workspace = () => ({
        id: EditorKey_Pathway,
        key: workspaceKey_ManagerPathway,
        instances: {
            selector: this.renderSelector.bind(this),
            presenter: this.renderPresenter.bind(this),
        },
        renderers: {
            horizontalSpace: TS_HorizontalWorkspace,
        }
    });
    forcePathwayEdit = false;
    permissionKey_Edit = ModuleFE_Pathway.permissions.collection.edit;
    // ######################## Lifecycle ########################
    __onPathwayStateSelected = (pathwayStateId) => {
        const pathwayState = ModuleFE_PathwayState.cache.unique(pathwayStateId);
        if (!pathwayState)
            throw new MUSTNeverHappenException(`Could not get pathway state for id ${pathwayStateId}`);
        this.setState({
            selectedPathwayState: new EditableDBItemV3(pathwayState, ModuleFE_PathwayState),
            selectedPathwayTransition: undefined,
            editResolutionFunction: false
        });
    };
    __onPathwayTransitionSelected = (pathwayTransitionId) => {
        const pathwayTransition = ModuleFE_PathwayTransition.cache.unique(pathwayTransitionId);
        if (!pathwayTransition)
            throw new MUSTNeverHappenException(`Could not find transition for id ${pathwayTransitionId}`);
        this.setState({
            selectedPathwayState: undefined,
            selectedPathwayTransition: new EditableDBItemV3(pathwayTransition, ModuleFE_PathwayTransition),
            editMode: false,
            editResolutionFunction: false,
        });
    };
    constructor(p) {
        super(p);
        // this.logger.setMinLevel(LogLevel.Verbose);
    }
    __onPathwayUpdated(...params) {
        const selectedPathwayId = this.state.selectedPathway?.item?._id;
        if (!selectedPathwayId)
            return;
        basicSyncEventRefresh(selectedPathwayId, params, () => this.functions.selectPathway(selectedPathwayId), () => this.functions.deselectPathway());
    }
    __onFocusResolutionFunction(transitionId, editable, postSave) {
        const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
        if (postSave && (this.state.selectedPathwayTransition && this.state.selectedPathwayTransition.item._id !== transitionId))
            return;
        if (!transition)
            throw new MUSTNeverHappenException(`cannot find transition with Id ${transitionId}`);
        this.setState({
            selectedPathwayTransition: new EditableDBItemV3(transition, ModuleFE_PathwayTransition),
            expressionEditable: editable
        });
    }
    shouldReDeriveState(nextProps) {
        return true;
    }
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        const selectedResult = state.selectedResult;
        if (!selectedResult)
            return state;
        let selectedPathwayId;
        let selectedPathwayStateId;
        let selectedPathwayTransitionId;
        switch (selectedResult.type) {
            case Page_PathwayEditor.SearchableItem_Pathway.type:
                selectedPathwayId = selectedResult.id;
                ModuleFE_PathwayEditor.setViewedPathwayId(selectedPathwayId);
                break;
            default:
                // return state;
                throw new ImplementationMissingException(`Missing implementation for search item type ${selectedResult.type}`);
        }
        //Set selected pathway
        if (selectedPathwayId) {
            const selectedPathway = ModuleFE_Pathway.cache.unique(selectedPathwayId);
            if (!selectedPathway)
                throw new MUSTNeverHappenException(`Could not find pathway for id ${selectedPathwayId}`);
            state.selectedPathway = new EditableDBItemV3(selectedPathway, ModuleFE_Pathway);
        }
        else {
            state.selectedPathway = undefined;
        }
        //Set selected pathway state
        if (selectedPathwayStateId) {
            const selectedPathwayState = ModuleFE_PathwayState.cache.unique(selectedPathwayStateId);
            if (!selectedPathwayState)
                throw new MUSTNeverHappenException(`Could not find pathway state for id ${selectedPathwayStateId}`);
            state.selectedPathwayState = new EditableDBItemV3(selectedPathwayState, ModuleFE_PathwayState);
        }
        else {
            state.selectedPathwayState = undefined;
        }
        //Set selected pathway transition
        if (selectedPathwayTransitionId) {
            const selectedPathwayTransition = ModuleFE_PathwayTransition.cache.unique(selectedPathwayTransitionId);
            if (!selectedPathwayTransition)
                throw new MUSTNeverHappenException(`Could not find pathway transition for id ${selectedPathwayTransitionId}`);
            state.selectedPathwayTransition = new EditableDBItemV3(selectedPathwayTransition, ModuleFE_PathwayTransition);
        }
        else {
            state.selectedPathwayTransition = undefined;
        }
        return state;
    }
    componentDidUpdate(prevProps, prevState) {
        if (this.forcePathwayEdit)
            this.forcePathwayEdit = false;
        if (prevState.editResolutionFunction && !this.state.editResolutionFunction) {
            // Close panels if needed
            dispatch_showResolutionFunctionEditorPanel.dispatchUI({});
        }
    }
    // ######################## Logic ########################
    onCreateNewPathway = () => {
        this.setState({
            selectedPathway: new EditableDBItemV3({}, ModuleFE_Pathway),
            editMode: true,
        });
    };
    getNewPathwayState = (pathwayId) => {
        return new EditableDBItemV3(Empty_PathwayState(pathwayId), ModuleFE_PathwayState);
    };
    functions = {
        selectPathway: (pathwayId, editMode) => {
            ModuleFE_PathwayEditor.setViewedPathwayId(pathwayId);
            this.onSearchResultSelected(pathwayId, Page_PathwayEditor.SearchableItem_Pathway.type, {
                editMode,
                selectedPathwayState: undefined,
                selectedPathwayTransition: undefined,
            });
        },
        selectPathwayState: (state, editMode = false) => {
            let editable;
            if (typeof state === 'string') {
                const pathwayState = ModuleFE_PathwayState.cache.unique(state);
                if (!pathwayState)
                    throw new MUSTNeverHappenException(`Could not get pathway state for id ${state}`);
                editable = new EditableDBItemV3(pathwayState, ModuleFE_PathwayState);
            }
            else
                editable = state;
            this.setState({
                selectedPathwayState: editable,
                selectedPathwayTransition: undefined,
                editMode: editMode,
            });
        },
        selectPathwayTransition: (transition, editMode = false) => {
            let editable;
            if (typeof transition === 'string') {
                const pathwayTransition = ModuleFE_PathwayTransition.cache.unique(transition);
                if (!pathwayTransition)
                    throw new MUSTNeverHappenException(`Could not get pathway transition for id ${transition}`);
                editable = new EditableDBItemV3(pathwayTransition, ModuleFE_PathwayTransition);
            }
            else
                editable = transition;
            this.setState({ selectedPathwayTransition: editable, editMode: editMode });
        },
        selectResolutionFunctionEditor: async (transitionId) => {
            let sourceState;
            let updateTransition = {};
            if (transitionId) {
                const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
                if (!transition)
                    throw new MUSTNeverHappenException(`transition with id ${transitionId} not found`);
                sourceState = ModuleFE_PathwayState.cache.unique(transition.sourceId);
                if (!sourceState)
                    throw new MUSTNeverHappenException(`couldn't find pathway state with id ${transition.sourceId}`);
                if (!this.state.selectedPathwayTransition)
                    updateTransition = {
                        selectedPathwayTransition: new EditableDBItemV3(transition, ModuleFE_PathwayTransition)
                    };
            }
            this.setState({
                ...updateTransition,
                selectedPathwayState: sourceState ? new EditableDBItemV3(sourceState, ModuleFE_PathwayState) : this.state.selectedPathwayState,
                editResolutionFunction: true
            });
        },
        deselectPathway: () => {
            ModuleFE_PathwayEditor.setViewedPathwayId(); // remove pathway id context
            this.clearSelectedResult();
            this.setState({
                editResolutionFunction: undefined,
                editMode: undefined,
                selectedResult: undefined,
                selectedPathway: undefined,
                selectedPathwayState: undefined,
                selectedPathwayTransition: undefined,
            });
        },
        deselectPathwayState: (editMode = false) => {
            this.setState({
                editMode: editMode,
                editResolutionFunction: false,
                selectedPathwayState: undefined,
                selectedPathwayTransition: undefined,
            });
        },
        deselectPathwayTransition: () => {
            this.setState({
                editMode: false,
                selectedPathwayTransition: undefined,
            });
        },
        deselectResolutionFunctionEditor: async () => {
            //close panel if needed
            dispatch_showResolutionFunctionEditorPanel.dispatchUI({});
            this.setState({
                editResolutionFunction: false,
                expressionEditable: undefined
            });
        },
        openPathwayEdit: () => {
            this.setState({
                editMode: true,
                selectedPathwayState: undefined,
                selectedPathwayTransition: undefined,
            });
        },
    };
    getModeKey = () => {
        return this.state.editMode ? editKey : viewKey;
    };
    toggleEdit = () => {
        this.setState({ editMode: !this.state.editMode });
    };
    // ######################## Render - Selectors ########################
    renderSelector() {
        if (this.state.editResolutionFunction)
            return this.renderResolutionFunctionEditor();
        if (this.state.selectedPathwayTransition)
            return this.pathwayTransitionEditor(this.state.selectedPathwayTransition);
        if (this.state.selectedPathwayState)
            return this.pathwayStateEditor(this.state.selectedPathwayState);
        if (this.state.selectedPathway)
            return this.pathwayEditor(this.state.selectedPathway);
        return _jsx(PathwaySelector_Search, { onCreateNewPathwayCallback: this.onCreateNewPathway, selectedItem: this.state.selectedResult, onSearchItemSelected: this.onSearchResultSelected });
    }
    pathwayEditor(selectedPathway) {
        return _jsx(Component_SwitchView, { mode: this.getModeKey(), modes: [
                {
                    key: viewKey,
                    renderer: () => _jsx(Renderer_PathwayDetails, { editable: selectedPathway, toggleMode: this.toggleEdit, functions: this.functions, addNewPathwayState: () => {
                            this.setState({ selectedPathwayState: this.getNewPathwayState(selectedPathway.item._id) });
                        } })
                },
                {
                    key: editKey,
                    permissionKey: this.permissionKey_Edit,
                    renderer: () => _jsx(Renderer_PathwayEditor, { editable: selectedPathway, toggleMode: this.toggleEdit, functions: this.functions, onCancel: () => {
                            const selectedPathway = this.state.selectedPathway;
                            if (!selectedPathway?.item._id)
                                return this.functions.deselectPathway();
                            this.onSearchResultSelected(selectedPathway.item._id, Page_PathwayEditor.searchItemKey_Pathway);
                            this.setState({ editMode: false });
                        } })
                }
            ] });
    }
    pathwayStateEditor(selectedPathwayState) {
        return _jsx(Component_SwitchView, { mode: this.getModeKey(), modes: [
                {
                    key: viewKey,
                    renderer: () => _jsx(Renderer_PathwayStateDetails, { pathwayState: selectedPathwayState, toggleEdit: this.toggleEdit, functions: this.functions })
                },
                {
                    key: editKey,
                    permissionKey: this.permissionKey_Edit,
                    renderer: () => _jsx(Renderer_PathwayStateEditor, { editable: selectedPathwayState, functions: this.functions, toggleMode: this.toggleEdit })
                }
            ] });
    }
    pathwayTransitionEditor(selectedPathwayTransition) {
        return _jsx(Component_SwitchView, { mode: this.getModeKey(), modes: [
                {
                    key: viewKey,
                    renderer: () => _jsx(Renderer_PathwayTransitionDetails, { pathwayTransition: selectedPathwayTransition, functions: this.functions, toggleEdit: this.toggleEdit })
                },
                {
                    key: editKey,
                    permissionKey: this.permissionKey_Edit,
                    renderer: () => _jsx(Renderer_PathwayTransitionEditor, { pathwayTransition: selectedPathwayTransition, toggleEdit: this.toggleEdit, functions: this.functions })
                }
            ] });
    }
    renderResolutionFunctionEditor() {
        let pathwayTransitions = ModuleFE_PathwayTransition.cache.filter(transition => (transition.sourceId === this.state.selectedPathwayState?.item?._id && transition.pathwayIds.includes(this.state.selectedPathway?.item._id)));
        pathwayTransitions = sortArray(pathwayTransitions, item => item.priorityIndex);
        return _jsx(Component_ResolutionFunctionEditor, { sourceStateId: pathwayTransitions[0].sourceId, selectedTransitionId: this.state.selectedPathwayTransition?.get('_id'), pathwayTransitionIds: pathwayTransitions.map(_transition => _transition._id), functions: this.functions });
        // return <Component_SwitchView
        // 	mode={editKey}
        // 	modes={[{
        // 		key: editKey,
        // 		permissionKey: this.permissionKey_Edit,
        // 		renderer: () => <Component_ResolutionFunctionEditor
        // 			selectedTransitionId={this.state.selectedPathwayTransition?.get('_id')}
        // 			pathwayTransitionIds={pathwayTransitions.map(_transition => _transition._id)}
        // 			functions={this.functions}
        // 		/>
        // 	}]}/>;
    }
    renderResolutionFunctionUtils() {
        if (!this.state.expressionEditable)
            return _jsx(_Fragment, {});
        return _jsx(Panel_ExpressionSidePanel, { editable: this.state.expressionEditable, defaultTab: UtilsTab_PrettyPrintV2, additionalTabs: [{
                    title: 'Pathway',
                    content: this.renderGraphView(),
                    uid: 'pathway-renderer'
                }] });
    }
    // ######################## Render - Presenters ########################
    renderGraphView = () => {
        return _jsx(Presenter_GraphViewWrapper, { pathwayId: this.state.selectedPathway?.item._id, editablePathwayState: this.state.selectedPathwayState, editablePathwayTransition: this.state.selectedPathwayTransition });
    };
    renderPresenter() {
        if (this.state.editResolutionFunction)
            return this.renderResolutionFunctionUtils();
        return this.renderGraphView();
    }
}
//# sourceMappingURL=Page_PathwayEditor.js.map